/**
 * 🔮 THE ORACLE - Predictive Content Intelligence
 * ═══════════════════════════════════════════════════════════════
 * 
 * Know if your content will succeed... BEFORE you publish.
 * 
 * ═══════════════════════════════════════════════════════════════
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { 
  Loader2, Eye, Zap, Target, AlertTriangle, TrendingUp, 
  Clock, CheckCircle, XCircle, Sparkles, BarChart3, 
  Lightbulb, ArrowRight, Copy, Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════
// HELPER COMPONENTS
// ═══════════════════════════════════════════════════════════════

const ScoreRing = ({ score, size = 120, label, color }) => {
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  
  const colorMap = {
    green: { stroke: '#22c55e', bg: 'rgba(34, 197, 94, 0.1)', text: 'text-green-400' },
    blue: { stroke: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)', text: 'text-blue-400' },
    yellow: { stroke: '#eab308', bg: 'rgba(234, 179, 8, 0.1)', text: 'text-yellow-400' },
    red: { stroke: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)', text: 'text-red-400' },
    purple: { stroke: '#a855f7', bg: 'rgba(168, 85, 247, 0.1)', text: 'text-purple-400' },
  };
  
  const colors = colorMap[color] || colorMap.purple;
  
  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg className="transform -rotate-90" width={size} height={size}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="transparent"
            stroke="rgba(255,255,255,0.1)"
            strokeWidth="8"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="transparent"
            stroke={colors.stroke}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            style={{ transition: 'stroke-dashoffset 1s ease-out' }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center flex-col">
          <span className={`text-3xl font-bold ${colors.text}`}>{score}</span>
          <span className="text-xs text-gray-500">/ 100</span>
        </div>
      </div>
      {label && <span className="mt-2 text-sm text-gray-400">{label}</span>}
    </div>
  );
};

const PredictionCard = ({ icon: Icon, label, value, reasoning, color, isLevel = false }) => {
  const colorMap = {
    green: 'border-green-500/30 bg-green-500/5',
    blue: 'border-blue-500/30 bg-blue-500/5',
    yellow: 'border-yellow-500/30 bg-yellow-500/5',
    red: 'border-red-500/30 bg-red-500/5',
    purple: 'border-purple-500/30 bg-purple-500/5',
  };
  
  const textColor = {
    green: 'text-green-400',
    blue: 'text-blue-400',
    yellow: 'text-yellow-400',
    red: 'text-red-400',
    purple: 'text-purple-400',
  };
  
  return (
    <div className={`p-4 rounded-xl border ${colorMap[color] || colorMap.purple}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-5 h-5 ${textColor[color] || 'text-purple-400'}`} />
        <span className="text-sm text-gray-400">{label}</span>
      </div>
      <div className={`text-2xl font-bold ${textColor[color] || 'text-white'} mb-1`}>
        {isLevel ? value : `${value}%`}
      </div>
      {reasoning && (
        <p className="text-xs text-gray-500 leading-relaxed">{reasoning}</p>
      )}
    </div>
  );
};

const InsightList = ({ title, items, icon: Icon, color }) => {
  const iconColors = {
    green: 'text-green-400',
    yellow: 'text-yellow-400',
    blue: 'text-blue-400',
  };
  
  return (
    <div className="p-4 rounded-xl bg-gray-800/50 border border-gray-700">
      <h4 className={`font-semibold mb-3 flex items-center gap-2 ${iconColors[color] || 'text-white'}`}>
        <Icon className="w-5 h-5" />
        {title}
      </h4>
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
            <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${iconColors[color]}`} />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN ORACLE COMPONENT
// ═══════════════════════════════════════════════════════════════

export const OraclePredictor = ({ onClose }) => {
  const [content, setContent] = useState('');
  const [headline, setHeadline] = useState('');
  const [contentType, setContentType] = useState('blog_post');
  const [platform, setPlatform] = useState('blog');
  const [audience, setAudience] = useState('');
  const [industry, setIndustry] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [prediction, setPrediction] = useState(null);
  const [error, setError] = useState(null);
  
  const contentTypes = [
    { value: 'blog_post', label: 'Blog Post' },
    { value: 'social_post', label: 'Social Post' },
    { value: 'email', label: 'Email' },
    { value: 'landing_page', label: 'Landing Page' },
    { value: 'ad_copy', label: 'Ad Copy' },
  ];
  
  const platforms = [
    { value: 'blog', label: 'Blog/Website' },
    { value: 'linkedin', label: 'LinkedIn' },
    { value: 'twitter', label: 'Twitter/X' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'email', label: 'Email' },
  ];

  const handlePredict = async () => {
    if (content.length < 50) {
      setError('Content must be at least 50 characters');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/oracle/predict`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content,
          headline,
          content_type: contentType,
          target_platform: platform,
          target_audience: audience,
          industry
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Prediction failed');
      }
      
      setPrediction(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setPrediction(null);
    setContent('');
    setHeadline('');
  };

  // Results view
  if (prediction) {
    return (
      <OracleResults 
        prediction={prediction} 
        onReset={handleReset}
        onClose={onClose}
      />
    );
  }

  // Input form view
  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/30 mb-4">
            <Eye className="w-4 h-4 text-purple-400" />
            <span className="text-purple-400 text-sm font-medium">THE ORACLE</span>
          </div>
          <h1 className="text-4xl font-bold mb-2">
            <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
              Predictive Content Intelligence
            </span>
          </h1>
          <p className="text-gray-400 text-lg">
            Know if your content will succeed... <span className="text-purple-400">before you publish</span>
          </p>
        </div>

        {/* Input Form */}
        <div className="bg-gray-800/50 rounded-2xl border border-gray-700 p-6 space-y-6">
          {/* Headline */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Headline / Title (optional)
            </label>
            <input
              type="text"
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
              placeholder="Enter your headline..."
              className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Content <span className="text-gray-500">(min 50 characters)</span>
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Paste your content here... The Oracle will analyze its potential."
              rows={8}
              className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all resize-none"
            />
            <div className="mt-1 text-xs text-gray-500 text-right">
              {content.length} characters
            </div>
          </div>

          {/* Options Grid */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Content Type</label>
              <select
                value={contentType}
                onChange={(e) => setContentType(e.target.value)}
                className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white focus:border-purple-500 focus:outline-none"
              >
                {contentTypes.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Target Platform</label>
              <select
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white focus:border-purple-500 focus:outline-none"
              >
                {platforms.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Target Audience (optional)</label>
              <input
                type="text"
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                placeholder="e.g., SaaS founders, marketers..."
                className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Industry (optional)</label>
              <input
                type="text"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder="e.g., B2B SaaS, eCommerce..."
                className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400">
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            onClick={handlePredict}
            disabled={loading || content.length < 50}
            className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-purple-500/25"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Consulting The Oracle...
              </>
            ) : (
              <>
                <Eye className="w-5 h-5" />
                Reveal My Content's Future
              </>
            )}
          </button>

          <p className="text-center text-gray-500 text-sm">
            Analysis takes ~10-15 seconds
          </p>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// RESULTS COMPONENT
// ═══════════════════════════════════════════════════════════════

const OracleResults = ({ prediction, onReset, onClose }) => {
  const [copied, setCopied] = useState(false);
  
  const { predictions, insights, verdict, overall_score, recommendation, recommendation_color } = prediction;
  
  const handleCopy = () => {
    const text = `
🔮 THE ORACLE PREDICTION
━━━━━━━━━━━━━━━━━━━━━━━━
Overall Score: ${overall_score}/100
Verdict: ${verdict}

📊 Predictions:
• Virality: ${predictions.virality.score}/100
• Conversion: ${predictions.conversion.score}%
• Engagement: ${predictions.engagement.level}
• Audience Match: ${predictions.audience_match.score}%
• Risk: ${predictions.risk.level}

💪 Strengths:
${insights.strengths.map(s => `• ${s}`).join('\n')}

🎯 Improvements:
${insights.improvements.map(i => `• ${i}`).join('\n')}

⏰ Best Time: ${insights.optimal_publish_window}
    `.trim();
    
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const recommendationStyles = {
    green: 'from-green-500 to-emerald-500 border-green-500/50',
    blue: 'from-blue-500 to-cyan-500 border-blue-500/50',
    yellow: 'from-yellow-500 to-orange-500 border-yellow-500/50',
    red: 'from-red-500 to-rose-500 border-red-500/50',
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/30 mb-4">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <span className="text-purple-400 text-sm font-medium">PROPHECY REVEALED</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">The Oracle Has Spoken</h1>
        </div>

        {/* Main Score & Recommendation */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Overall Score */}
          <div className="bg-gray-800/50 rounded-2xl border border-gray-700 p-8 flex flex-col items-center justify-center">
            <ScoreRing 
              score={overall_score} 
              size={160} 
              label="Overall Score"
              color={recommendation_color}
            />
            <p className="mt-4 text-gray-400 text-center max-w-xs">
              {verdict}
            </p>
          </div>

          {/* Recommendation */}
          <div className={`rounded-2xl border-2 p-8 flex flex-col items-center justify-center bg-gradient-to-br ${recommendationStyles[recommendation_color] || recommendationStyles.purple}`}>
            <div className="text-6xl mb-4">
              {recommendation_color === 'green' ? '🚀' : 
               recommendation_color === 'blue' ? '👍' :
               recommendation_color === 'yellow' ? '⚠️' : '🛑'}
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">{recommendation}</h2>
            <p className="text-white/80 text-center">
              {recommendation_color === 'green' ? 'Your content is ready to dominate!' :
               recommendation_color === 'blue' ? 'Solid content with good potential.' :
               recommendation_color === 'yellow' ? 'Review the improvements below.' :
               'Significant changes needed before publishing.'}
            </p>
          </div>
        </div>

        {/* Prediction Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <PredictionCard
            icon={TrendingUp}
            label="Virality"
            value={predictions.virality.score}
            reasoning={predictions.virality.reasoning}
            color={predictions.virality.color}
          />
          <PredictionCard
            icon={Target}
            label="Conversion"
            value={predictions.conversion.score}
            reasoning={predictions.conversion.reasoning}
            color={predictions.conversion.color}
          />
          <PredictionCard
            icon={BarChart3}
            label="Engagement"
            value={predictions.engagement.level.toUpperCase()}
            reasoning={predictions.engagement.reasoning}
            color={predictions.engagement.color}
            isLevel
          />
          <PredictionCard
            icon={Eye}
            label="Audience Match"
            value={predictions.audience_match.score}
            reasoning={predictions.audience_match.reasoning}
            color={predictions.audience_match.color}
          />
          <PredictionCard
            icon={AlertTriangle}
            label="Risk Level"
            value={predictions.risk.level.toUpperCase()}
            color={predictions.risk.color}
            isLevel
          />
        </div>

        {/* Risk Factors */}
        {predictions.risk.factors && predictions.risk.factors.length > 0 && (
          <div className="mb-8 p-4 rounded-xl bg-red-500/10 border border-red-500/30">
            <h4 className="font-semibold text-red-400 flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5" />
              Risk Factors Identified
            </h4>
            <ul className="space-y-1">
              {predictions.risk.factors.map((factor, i) => (
                <li key={i} className="text-sm text-red-300 flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  {factor}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Insights Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <InsightList
            title="Content Strengths"
            items={insights.strengths}
            icon={CheckCircle}
            color="green"
          />
          <InsightList
            title="Recommended Improvements"
            items={insights.improvements}
            icon={Lightbulb}
            color="yellow"
          />
        </div>

        {/* Optimal Time */}
        <div className="mb-8 p-6 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30">
          <div className="flex items-center gap-3">
            <Clock className="w-8 h-8 text-blue-400" />
            <div>
              <h4 className="font-semibold text-white">Optimal Publish Window</h4>
              <p className="text-blue-300 text-lg">{insights.optimal_publish_window}</p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={handleCopy}
            className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl flex items-center justify-center gap-2 transition-all"
          >
            {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            {copied ? 'Copied!' : 'Copy Results'}
          </button>
          <button
            onClick={onReset}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-all"
          >
            <Zap className="w-5 h-5" />
            Analyze New Content
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="px-6 py-3 border border-gray-700 hover:bg-gray-800 text-gray-300 rounded-xl transition-all"
            >
              Close
            </button>
          )}
        </div>

        {/* Confidence */}
        <p className="text-center text-gray-500 text-sm mt-6">
          Oracle Confidence: {prediction.confidence}% • Prediction ID: {prediction.prediction_id}
        </p>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// COMPACT WIDGET VERSION (for landing page)
// ═══════════════════════════════════════════════════════════════

export const OracleWidget = () => {
  const [showOracle, setShowOracle] = useState(false);

  return (
    <>
      <div className="text-center">
        <button
          onClick={() => setShowOracle(true)}
          className="group relative px-8 py-4 bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 hover:from-purple-500 hover:via-pink-500 hover:to-purple-500 text-white font-bold rounded-xl transition-all shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 flex items-center gap-3 mx-auto"
        >
          <Eye className="w-6 h-6" />
          <span>Predict My Content's Future</span>
          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </button>
        <p className="mt-3 text-gray-500 text-sm">
          🔮 Know if your content will succeed before you publish
        </p>
      </div>
      
      {/* Use Portal to render modal at document body level */}
      {showOracle && createPortal(
        <div className="fixed inset-0 bg-black/95 z-[9999] overflow-y-auto">
          <OraclePredictor onClose={() => setShowOracle(false)} />
        </div>,
        document.body
      )}
    </>
  );
};

export default OraclePredictor;
